/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import cookies from 'js/lib/cookie';

import { Button } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';

import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/front-page';

type Props = {
  children: React.ReactNode;
  trackingName: string;
};

type State = {
  bannerHidden: boolean;
};

const styles: Record<string, Interpolation<Theme>> = {
  dismissableBanner: (theme: Theme) => css`
    .banner-alert {
      align-items: center;
      background-color: #d6edf6;
      border-width: 0;
      border-radius: 0;
      color: #000;
      display: flex;
      font-size: 16px;
      justify-content: center;
      margin: 0;
      min-height: 80px;
      padding: 15px 40px;
      position: relative;

      &.isLohp {
        font-family: Arial, sans-serif;
      }

      a {
        /* Need to use important here to override the color; using object syntax and '&&': {} doesn't work */
        color: ${theme.palette.black[500]} !important;
        display: inline;
        font-weight: bold;
        text-decoration: underline;

        &:hover {
          text-decoration: none;
        }
      }
    }

    /* I'm overriding default CDS button styles to match the button from the non-CDS logged-out homepage */
    .close-button {
      margin-left: -24px;
      height: 36px;
      width: 36px;
      border: 1px solid rgb(102 102 102);
      border-radius: 2px;
      box-shadow: none;
      display: flex;
      justify-content: center;
      transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms;
      transform: translateX(69px);

      &:hover {
        box-shadow: none;
      }

      @media screen and (min-width: 0) and (max-width: 1150px) {
        transform: translateX(30px);
      }

      @media (min-width: 1151px) and (max-width: 1250px) {
        transform: translateX(40px);
      }

      .cds-button-endIcon {
        margin-left: 0;
      }
    }
  `,
};

class DismissableBanner extends React.Component<Props, State> {
  cookieName: string;

  constructor(props: Props) {
    super(props);
    const { trackingName } = props;

    this.cookieName = `alert_closed_${trackingName}`;

    const cookieValue = cookies.get(this.cookieName);

    this.state = {
      bannerHidden: cookieValue === 'true',
    };
  }

  handleClose = () => {
    this.setState(() => ({
      bannerHidden: true,
    }));
    cookies.set(this.cookieName, 'true');
  };

  render() {
    const { trackingName, children } = this.props;
    const { bannerHidden } = this.state;

    if (bannerHidden) {
      return null;
    }

    return (
      <TrackedDiv
        css={styles.dismissableBanner}
        trackingName={trackingName}
        trackClicks={false}
        withVisibilityTracking
        data-unit="dismissable-banner"
      >
        <div role="alert" className="banner-alert isLohp">
          {children}
          <Button
            aria-label={_t('Close banner')}
            className="close-button"
            component={TrackedButton}
            trackingName={`${trackingName}_close`}
            withVisibilityTracking={false}
            requireFullyVisible={false}
            icon={<CloseIcon size="small" color="default" title="Close banner" />}
            onClick={this.handleClose}
            variant="secondary"
            size="small"
            // Button requires a child, but this button doesn't need any because it just renders the close icon.
            // If I try to add a nested empty string, it is removed when I make a commit, thus causing a TS error
            // eslint-disable-next-line react/no-children-prop
            children=""
          />
        </div>
      </TrackedDiv>
    );
  }
}

export default DismissableBanner;
